import { type FormEvent, useState } from "react";
import { Lock, User as UserIcon } from "lucide-react";
import { api, ApiError, type User } from "./api";
import { Spinner } from "./ui";

export function Login({ onLogin }: { onLogin: (user: User) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      onLogin((await api.login(username, password)).user);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not reach the server");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-navy-900 bg-[radial-gradient(ellipse_at_top,rgba(217,178,92,0.18),transparent_60%)] px-5">
      <div className="mb-8 flex flex-col items-center text-center">
        <img src="/brand/emblem.png" alt="Oh-Ho Collectibles" className="h-24 w-24 drop-shadow-xl" />
        <div className="mt-4 text-[22px] font-semibold tracking-tight text-white">Price Check</div>
        <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold-400">Oh-Ho Collectibles</div>
      </div>
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-lift">
        <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="username">Username</label>
        <div className="relative mb-4">
          <UserIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            id="username"
            className="field h-12 pl-10"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="password">Password</label>
        <div className="relative mb-5">
          <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            id="password"
            type="password"
            className="field h-12 pl-10"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error && <p className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
        <button type="submit" disabled={busy || !username || !password} className="btn-primary w-full">
          {busy && <Spinner />}
          {busy ? "Logging in" : "Log in"}
        </button>
      </form>
      <p className="mt-6 text-xs text-navy-200/70">Card prices from real sold sales</p>
    </div>
  );
}
